import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Button from 'shopper/components/Button';

import CtaCard from 'components/Coupons/CtaCard';
import SkeletonGroup from 'components/Skeleton/SkeletonGroup';

import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import useMediaQuery from 'hooks/useMediaQuery';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

import { useCouponDeepLink } from 'providers/CouponDeepLinkProvider';
import { useUser } from 'providers/UserProvider';

import CouponListItem from './CouponListItem';

import COUPONS from 'constants/coupons';

const CouponListItemSkeleton = dynamic(
  () => import('./CouponListItem/Skeleton'),
  { ssr: false }
);
const EmptyList = dynamic(() => import('./EmptyList'));

const couponShape = PropTypes.shape({
  couponId: PropTypes.number.isRequired,
  couponDiscountValue: PropTypes.string.isRequired,
  couponDiscountShort: PropTypes.string.isRequired,
  couponDiscount: PropTypes.string.isRequired,
  couponDiscountOn: PropTypes.string.isRequired,
  couponStatusName: PropTypes.string.isRequired,
  couponInstructions: PropTypes.string.isRequired,
  couponUntil: PropTypes.string,
  couponVerified: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  storeDomain: PropTypes.string.isRequired,
  storeSlug: PropTypes.string.isRequired,
  couponTag: PropTypes.arrayOf(PropTypes.string.isRequired),
  couponPublished: PropTypes.string,
  couponTitle: PropTypes.string.isRequired,
  couponCode: PropTypes.string.isRequired,
  couponUrl: PropTypes.string.isRequired,
  userName: PropTypes.string,
});

const CouponItems = ({
  coupons,
  gaEventCategory,
  isCouponVisible,
  withCouponStoreCover,
}) =>
  coupons.map(
    ({
      couponCode,
      couponDiscountShort,
      couponId,
      couponInstructions,
      couponTag,
      couponTitle,
      couponUntil,
      couponVerified,
      storeImage,
      storeName,
      storeSlug,
      visibility,
    }) => (
      <CouponListItem
        key={couponId}
        couponCode={couponCode}
        couponDiscountShort={couponDiscountShort}
        couponId={couponId}
        couponInstructions={couponInstructions}
        couponTag={couponTag}
        couponTitle={couponTitle}
        couponUntil={couponUntil}
        couponVerified={couponVerified}
        gaEventCategory={gaEventCategory}
        isCouponVisible={isCouponVisible}
        storeImage={storeImage}
        storeName={storeName}
        storeSlug={storeSlug}
        visibility={visibility}
        withCouponStoreCover={withCouponStoreCover}
      />
    )
  );

/**
 * Static mode: receives `coupons` array directly, no pagination.
 * Used by store coupon pages where SSR delivers the final list.
 */
const { DESKTOP_SEQUENCE, MOBILE_SEQUENCE } = COUPONS.CTA_CONFIG;
const APP_SMARTLINK = process.env.NEXT_PUBLIC_COUPONS_LIST_APP_SMARTLINK;

const StaticCouponsList = ({
  className,
  coupons,
  gaEventCategory,
  storeId,
  storeName,
  storeSlug,
  withCouponStoreCover = false,
}) => {
  const { isCouponsCodesVisible } = useCouponDeepLink();
  const { isLoggedIn } = useUser();
  const { isMobile } = useMediaQuery();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const ctaSequence = useMemo(() => {
    if (!isMounted || isLoggedIn) return [];
    const sequence = isMobile ? MOBILE_SEQUENCE : DESKTOP_SEQUENCE;
    return APP_SMARTLINK
      ? sequence
      : sequence.filter((cta) => cta.type !== 'app-download');
  }, [isLoggedIn, isMobile, isMounted]);

  const showCtas = ctaSequence.length > 0 && storeSlug;

  const ctaPositions = useMemo(() => {
    const map = new Map();
    ctaSequence.forEach((cta) => map.set(cta.afterCoupon, cta.type));
    return map;
  }, [ctaSequence]);

  const intercalatedItems = useMemo(() => {
    if (!showCtas) return null;

    const result = [];

    coupons.forEach((coupon, index) => {
      result.push(
        <CouponListItem
          key={coupon.couponId}
          couponCode={coupon.couponCode}
          couponDiscountShort={coupon.couponDiscountShort}
          couponId={coupon.couponId}
          couponInstructions={coupon.couponInstructions}
          couponTag={coupon.couponTag}
          couponTitle={coupon.couponTitle}
          couponUntil={coupon.couponUntil}
          couponVerified={coupon.couponVerified}
          gaEventCategory={gaEventCategory}
          isCouponVisible={isCouponsCodesVisible}
          storeImage={coupon.storeImage}
          storeName={coupon.storeName}
          storeSlug={coupon.storeSlug}
          visibility={coupon.visibility}
          withCouponStoreCover={withCouponStoreCover}
        />
      );

      const ctaType = ctaPositions.get(index + 1);
      if (ctaType) {
        result.push(
          <CtaCard
            key={`cta-${index}`}
            storeId={storeId}
            storeImage={coupons[0]?.storeImage}
            storeName={storeName}
            storeSlug={storeSlug}
            type={ctaType}
          />
        );
      }
    });

    return result;
  }, [
    showCtas,
    coupons,
    ctaPositions,
    gaEventCategory,
    isCouponsCodesVisible,
    storeName,
    storeSlug,
    withCouponStoreCover,
  ]);

  if (coupons.length === 0) {
    return <EmptyList />;
  }

  if (!showCtas) {
    return (
      <section className={twMerge('flex flex-col gap-y-6', className)}>
        <CouponItems
          coupons={coupons}
          gaEventCategory={gaEventCategory}
          isCouponVisible={isCouponsCodesVisible}
          withCouponStoreCover={withCouponStoreCover}
        />
      </section>
    );
  }

  return (
    <section className={twMerge('flex flex-col gap-y-6', className)}>
      {intercalatedItems}
    </section>
  );
};

/**
 * Paginated mode: uses useInfiniteQuery for client-side pagination.
 * Used by promobit coupons page and other paginated listings.
 */
const PaginatedCouponsList = ({
  className,
  gaEventCategory,
  initialCoupons,
  queryFn,
  queryKey,
  shouldShowTruePagination = true,
  seeMoreButtonLink = null,
  withCouponStoreCover = false,
}) => {
  const router = useRouter();
  const { isCouponsCodesVisible } = useCouponDeepLink();
  const [isPaginationEnabled, setPaginationEnabled] = useState(false);
  const fetchNextPageRef = useRef(null);

  const {
    data: coupons,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: (page, { signal }) =>
      queryFn({ page, limit: COUPONS.COUPONS_LIMIT }, { signal }),
    select: (_coupons) =>
      _coupons.filter(
        (coupon) => coupon.couponStatusName !== COUPONS.STATUS.FINISHED
      ),
    getNextPageParam: (lastCoupons, pages) => {
      if (
        lastCoupons.coupons?.at(-1).couponStatusName ===
          COUPONS.STATUS.FINISHED ||
        lastCoupons.coupons?.length < COUPONS.COUPONS_LIMIT ||
        !shouldShowTruePagination
      ) {
        return null;
      }

      return pages.length + 1;
    },
    flattenKey: 'coupons',
    initialData: initialCoupons,
    staleTime: 0,
    cacheTime: 0.5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    const resetInternalStates = () => {
      setPaginationEnabled(false);
    };

    router.events.on('routeChangeComplete', resetInternalStates);

    return () => {
      router.events.off('routeChangeComplete', resetInternalStates);
    };
  }, []);

  useIntersectionObserver({
    enabled: isPaginationEnabled && hasNextPage,
    target: fetchNextPageRef,
    rootMargin: '1200px',
    threshold: 0,
    onIntersect: fetchNextPage,
  });

  const onEnablePagination = () => {
    if (shouldShowTruePagination) {
      setPaginationEnabled(true);
    }

    sendEvent({ action: 'see_more_coupons', category: gaEventCategory });
  };

  const isListLoading = isFetchingNextPage || isLoading;
  const showPaginate = !isListLoading && !isPaginationEnabled;

  if (isSuccess && coupons.length === 0) {
    return <EmptyList />;
  }

  return (
    <>
      <section className={twMerge('flex flex-col gap-y-6', className)}>
        {isSuccess && (
          <CouponItems
            coupons={coupons}
            gaEventCategory={gaEventCategory}
            isCouponVisible={isCouponsCodesVisible}
            withCouponStoreCover={withCouponStoreCover}
          />
        )}
        {showPaginate && shouldShowTruePagination && (
          <Button className="mx-auto" size="size1" onClick={onEnablePagination}>
            {placeholder('actions.loadMoreCoupons')}
          </Button>
        )}
        {seeMoreButtonLink && !shouldShowTruePagination && (
          <NextLink href={seeMoreButtonLink} passHref>
            <Button as="a" className="mx-auto" size="size1">
              {placeholder('actions.loadMoreCoupons')}
            </Button>
          </NextLink>
        )}
        {isListLoading && (
          <SkeletonGroup items={6} skeleton={CouponListItemSkeleton} />
        )}
      </section>
      <div ref={fetchNextPageRef} />
    </>
  );
};

const CouponsList = ({ coupons, ...props }) => {
  if (coupons) {
    return <StaticCouponsList coupons={coupons} {...props} />;
  }

  return <PaginatedCouponsList {...props} />;
};

CouponsList.propTypes = {
  className: PropTypes.string,
  coupons: PropTypes.arrayOf(couponShape),
  gaEventCategory: PropTypes.string.isRequired,
  initialCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(couponShape),
  }),
  queryFn: PropTypes.func,
  queryKey: PropTypes.array,
  seeMoreButtonLink: PropTypes.string,
  shouldShowTruePagination: PropTypes.bool,
  storeId: PropTypes.number,
  storeName: PropTypes.string,
  storeSlug: PropTypes.string,
  withCouponStoreCover: PropTypes.bool,
};

export default CouponsList;
